// ============================================================
// pages/admin/AdminLoyalty.jsx — Loyalty Config (Firebase)
// ============================================================

import React, { useState, useEffect } from "react";
import { LoyaltyService } from "../../services/firebaseServices";
import { collection, getDocs, updateDoc, doc, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase.config";

export default function AdminLoyalty({ showToast }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    LoyaltyService.getConfig()
      .then((data) => {
        if (data) setConfig(data);
        else setConfig({
          pointsPerAmount: 1, amountThreshold: 10,
          tiers: [
            { name: "Bronze", minPoints: 0, discountPercent: 5, rewards: ["Welcome 5% discount"] },
            { name: "Silver", minPoints: 500, discountPercent: 8, rewards: ["Free cookie box", "8% discount"] },
            { name: "Gold", minPoints: 1000, discountPercent: 10, rewards: ["Free cake slice", "Priority booking", "10% discount"] },
            { name: "Platinum", minPoints: 2000, discountPercent: 15, rewards: ["Full cake", "VIP tastings", "Birthday special", "15% discount"] },
          ],
          birthdayBonusPoints: 100, referralBonusPoints: 50,
          doublePointsDays: ["saturday"],
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const update = (key, val) => setConfig((prev) => ({ ...prev, [key]: val }));

  const updateTier = (idx, key, val) => {
    const newTiers = [...config.tiers];
    newTiers[idx] = { ...newTiers[idx], [key]: key === "minPoints" || key === "discountPercent" ? Number(val) : val };
    update("tiers", newTiers);
  };

  const updateTierRewards = (idx, rewardsStr) => {
    const newTiers = [...config.tiers];
    newTiers[idx] = { ...newTiers[idx], rewards: rewardsStr.split(",").map((s) => s.trim()).filter(Boolean) };
    update("tiers", newTiers);
  };

  const toggleDoubleDay = (day) => {
    const days = config.doublePointsDays || [];
    if (days.includes(day)) {
      update("doublePointsDays", days.filter((d) => d !== day));
    } else {
      update("doublePointsDays", [...days, day]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await LoyaltyService.updateConfig(config);
      showToast("Loyalty config saved!", "success");
    } catch (err) {
      showToast("Failed to save config", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) return <div className="empty-state"><p>Loading loyalty config...</p></div>;

  const handleResetAll = async () => {
    const confirm1 = window.confirm("⚠️ Reset ALL customer loyalty points to 0 and tiers to Bronze?\n\nThis cannot be undone.");
    if (!confirm1) return;
    const confirm2 = window.prompt('Type "RESET" to confirm:');
    if (confirm2 !== "RESET") {
      showToast("Reset cancelled", "info");
      return;
    }
    setResetting(true);
    try {
      const snap = await getDocs(collection(db, "users"));
      let count = 0;
      for (const userDoc of snap.docs) {
        const data = userDoc.data();
        if (data.loyaltyPoints > 0 || data.loyaltyTier !== "bronze") {
          await updateDoc(doc(db, "users", userDoc.id), {
            loyaltyPoints: 0,
            loyaltyTier: "bronze",
          });
          // Log the reset as a transaction
          await addDoc(collection(db, "loyalty", "transactions", "all"), {
            customerId: userDoc.id,
            type: "reset",
            points: -(data.loyaltyPoints || 0),
            reason: "Annual loyalty points reset by admin",
            bookingId: null,
            balanceAfter: 0,
            createdAt: serverTimestamp(),
          });
          count++;
        }
      }
      showToast(`Reset complete! ${count} customer(s) reset to Bronze / 0 points`, "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to reset points", "error");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div>
      <div className="section-header">
        <div>
          <h2>Loyalty Program Config</h2>
          <p>Customise reward rules, tiers, and point-earning rates</p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button className="btn btn-danger" onClick={handleResetAll} disabled={resetting}
            style={{ fontSize: "0.85rem" }}>
            {resetting ? "Resetting..." : "🔄 Reset All Points"}
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "💾 Save All Changes"}
          </button>
        </div>
      </div>

      <div className="two-col">
        {/* Earning Rules */}
        <div>
          <div className="card" style={{ marginBottom: "1.5rem" }}>
            <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>Earning Rules</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <div className="form-group">
                <label className="form-label">Points Earned</label>
                <input className="form-input" type="number" value={config.pointsPerAmount}
                  onChange={(e) => update("pointsPerAmount", Number(e.target.value))} />
              </div>
              <div className="form-group">
                <label className="form-label">Per every Rs</label>
                <input className="form-input" type="number" value={config.amountThreshold}
                  onChange={(e) => update("amountThreshold", Number(e.target.value))} />
              </div>
            </div>
            <p style={{ fontSize: "0.82rem", color: "var(--cocoa)", marginTop: "0.5rem", background: "var(--cream)", padding: "0.5rem 0.75rem", borderRadius: 8 }}>
              Currently: <strong>{config.pointsPerAmount} point</strong> for every <strong>Rs {config.amountThreshold}</strong> spent
            </p>
          </div>

          {/* Special Bonuses */}
          <div className="card" style={{ marginBottom: "1.5rem" }}>
            <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>Special Bonuses</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <div className="form-group">
                <label className="form-label">Birthday Bonus Points</label>
                <input className="form-input" type="number" value={config.birthdayBonusPoints}
                  onChange={(e) => update("birthdayBonusPoints", Number(e.target.value))} />
                <div style={{ fontSize: "0.78rem", color: "var(--cocoa)", marginTop: 4 }}>
                  Auto-sent at 8 AM on customer's birthday
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Referral Bonus Points</label>
                <input className="form-input" type="number" value={config.referralBonusPoints}
                  onChange={(e) => update("referralBonusPoints", Number(e.target.value))} />
              </div>
            </div>
          </div>

          {/* Double Points Days */}
          <div className="card">
            <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>2× Points Days</h3>
            <p style={{ fontSize: "0.85rem", color: "var(--cocoa)", marginBottom: "0.75rem" }}>
              Select days when customers earn double points:
            </p>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => (
                <button key={day}
                  className={`filter-btn ${(config.doublePointsDays || []).includes(day) ? "active" : ""}`}
                  onClick={() => toggleDoubleDay(day)}
                  style={{ textTransform: "capitalize" }}>
                  {day.slice(0, 3)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tier Configuration */}
        <div className="card">
          <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>Reward Tiers</h3>
          {config.tiers.map((tier, idx) => (
            <div key={idx} style={{
              padding: "1rem", marginBottom: "0.75rem", borderRadius: 8,
              border: "1px solid rgba(196,136,58,0.15)", background: "var(--cream)",
            }}>
              <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
                <div className="form-group" style={{ flex: 1, margin: 0 }}>
                  <label className="form-label" style={{ fontSize: "0.75rem" }}>Tier Name</label>
                  <input className="form-input" value={tier.name}
                    onChange={(e) => updateTier(idx, "name", e.target.value)} />
                </div>
                <div className="form-group" style={{ flex: "0 0 90px", margin: 0 }}>
                  <label className="form-label" style={{ fontSize: "0.75rem" }}>Min Points</label>
                  <input className="form-input" type="number" value={tier.minPoints}
                    onChange={(e) => updateTier(idx, "minPoints", e.target.value)} />
                </div>
                <div className="form-group" style={{ flex: "0 0 90px", margin: 0 }}>
                  <label className="form-label" style={{ fontSize: "0.75rem" }}>Discount %</label>
                  <input className="form-input" type="number" value={tier.discountPercent}
                    onChange={(e) => updateTier(idx, "discountPercent", e.target.value)} />
                </div>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ fontSize: "0.75rem" }}>Rewards (comma-separated)</label>
                <input className="form-input" value={(tier.rewards || []).join(", ")}
                  onChange={(e) => updateTierRewards(idx, e.target.value)} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}